import type { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import type { AlignDimensionEntity } from '@/plugins/cad/module/CADModule/model/entity'
import { DimensionObject } from '@/plugins/cad/module/CADModule/object/entityObject/DimensionObject'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import { BaseObjectManager, HighlightMode } from './BaseObjectManager'

type DimensionEntity = AlignDimensionEntity

/**
 * 对齐标注实体的渲染对象管理器，驱动 DimensionObject 创建与高亮。
 */
export class DimensionObjectManager extends BaseObjectManager<DimensionEntity, DimensionObject> {
  // entity的原材质
  private entityOriginalMaterialMap: Map<string, LineMaterial> = new Map()
  private unitPresentation?: UnitPresentationService

  protected createObject(entity: DimensionEntity) {
    const material = this.getCachedMaterial(entity)
    const object = new DimensionObject(entity, material, undefined, this.unitPresentation)
    this.entityOriginalMaterialMap.set(entity.id, object.material)
    return object
  }

  protected onUpdateGeometry(entity: DimensionEntity): void {
    const object = this.objectMap.get(entity.id)
    if (!object) {
      return
    }
    object.onUpdateGeometry()
  }

  protected onUpdateMaterial(entity: DimensionEntity): void {
    const object = this.objectMap.get(entity.id)
    if (!object) {
      return
    }
    this.disposeMaterialFromObject(object)
    object.onUpdateMaterial(this.getCachedMaterial(entity))

    this.entityOriginalMaterialMap.set(entity.id, object.material)
    this.updateEntityHighlight(entity)
  }

  protected disposeObject(object: DimensionObject): void {
    this.disposeMaterialFromObject(object)
    object.dispose()
    this.entityOriginalMaterialMap.delete(object.userData.entity.id)
  }

  /** 绑定单位服务并刷新当前全部自动标注。 */
  setUnitPresentationService(service: UnitPresentationService): void {
    this.unitPresentation = service
    for (const object of this.objectMap.values()) {
      object.setUnitPresentationService(service)
    }
  }

  /** 刷新当前全部自动标注文字。 */
  refreshUnitPresentation(): void {
    for (const object of this.objectMap.values()) {
      object.refreshUnitPresentation()
    }
  }

  protected handleEntityHighlight(entity: DimensionEntity, mode: HighlightMode): void {
    const object = this.objectMap.get(entity.id)
    if (!object) {
      return
    }
    if (mode === HighlightMode.None) {
      // 取消高亮
      const material = this.entityOriginalMaterialMap.get(entity.id)!
      object.onUpdateMaterial(material)
    } else {
      // 高亮
      const material =
        mode === HighlightMode.Hover
          ? this.materialManager.getCachedHoverMaterial(entity)
          : this.materialManager.getCachedSelectMaterial(entity)
      const color = material.color.getHex()
      object.onUpdateMaterial(material, color)
    }
  }
}
